import { useTranslate } from 'module/hooks/use-translation';
import { NodeViewContent } from '@tiptap/react';
import {
  ParticipantGroupOption,
  CustomSectionState,
  ActionCustomSectionAttributes,
} from 'api/custom-section/types';
import { CustomSectionType } from 'api/custom-section/types';
import { useCallback, useState } from 'react';
import {
  SectionPreview,
  SectionTitle,
  SectionWrapper,
} from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-styles';
import { ActionPromptInput } from './prompt-input';
import {
  ActionPromptType,
  defaultParticipantsFilter,
} from '../../../../types/ui-types';
import { defaultCustomSectionNodeConfig } from '../../../../utils';
import { getConfigurationPlaceholderNodes } from '../../../shared/configuration-content-utils';
import { ActionSectionComponentProps } from '../types';

const constructPrompt = (attributes: ActionCustomSectionAttributes) => {
  const payload = {
    ...defaultCustomSectionNodeConfig[CustomSectionType.Action].config,
    ...attributes.config,
  };

  return {
    title: attributes.title,
    aiContextChecked: payload.aiContext ? true : false,
    aiContext: payload.aiContext,
    groupBy: payload.groupBy,
    generateAs: payload.generateAs,
    participants: payload.participants,
    participantsFilter:
      payload.participants === ParticipantGroupOption.Custom
        ? (payload.participantsFilter ?? defaultParticipantsFilter)
        : defaultParticipantsFilter,
  };
};

export const ActionPromptSection = ({
  node,
  onRemove,
  onDone,
  onUndo,
  updateContent,
  contentEditable,
  allowResolve,
  onOpen,
  pageType,
  pageTriggerListFilter,
}: ActionSectionComponentProps) => {
  const showPrompt = node.attrs.state === CustomSectionState.Open;
  const [prompt, setPrompt] = useState<ActionPromptType>(
    constructPrompt(node.attrs),
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const translate = useTranslate();

  const onGenerate = async (userPrompt: ActionPromptType) => {
    setIsGenerating(true);
    const attributes = {
      ...node.attrs,
      title: userPrompt.title,
      config: {
        aiContext: userPrompt.aiContext,
        groupBy: userPrompt.groupBy,
        generateAs: userPrompt.generateAs,
        participants: userPrompt.participants,
        participantsFilter:
          userPrompt.participants === ParticipantGroupOption.Custom
            ? userPrompt.participantsFilter
            : undefined,
      },
      state: CustomSectionState.Closed,
    };

    onDone(attributes).then(() => {
      updateContent?.(
        getConfigurationPlaceholderNodes(
          {
            groupBy: userPrompt.groupBy,
            generateAs: userPrompt.generateAs,
          },
          translate,
        ),
        attributes,
      );
      setIsGenerating(false);
    });
  };

  const getNodeAsPayload = useCallback(() => {
    const payload = node.toJSON();

    if (!('content' in payload)) {
      payload.content = [];
    }

    return payload;
  }, [node]);

  const onClose = useCallback(() => {
    if (node.content?.size === 0) {
      onRemove();

      return;
    }
    setPrompt(constructPrompt(node.attrs));
    onUndo();
  }, [onUndo, node.attrs, setPrompt, node.content?.size, onRemove]);

  return (
    <div>
      {showPrompt && contentEditable ? (
        <ActionPromptInput
          prompt={prompt}
          setPrompt={setPrompt}
          onGenerate={onGenerate}
          isGenerating={isGenerating}
          onCancel={onRemove}
          onUndo={onClose}
          allowResolve={allowResolve}
          getNodeAsPayload={getNodeAsPayload}
          pageType={pageType}
          pageTriggerListFilter={pageTriggerListFilter}
        />
      ) : (
        <SectionPreview onClick={() => onOpen()}>
          <SectionWrapper>
            <SectionTitle>{prompt.title}</SectionTitle>
            <NodeViewContent />
          </SectionWrapper>
        </SectionPreview>
      )}
    </div>
  );
};
